import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  ArgType,
  Condition,
  ConditionSet,
  ConditionSetElementType,
  Element,
  OPERATORS,
  QuestionType,
} from '@hyper-inferno/survey-elements-core';
import {
  LucideAngularModule,
  Plus,
  Trash2,
  GripVertical,
} from 'lucide-angular';
import { lastValueFrom } from 'rxjs';
import { ConditionRulesManagerService } from '../condition-rule-manager/condition-rule-manager-service/condition-rules-manager.service';
import { DataStoreService } from '../data-store-service/data-store.service';
import { QUESTION_TYPE_CONFIG } from '@hyper-inferno/skip-logic-core';

@Component({
  selector: 'lib-condition-rule-builder',
  imports: [CommonModule, FormsModule, DragDropModule, LucideAngularModule],
  templateUrl: './condition-rule-builder.html',
  styleUrl: './condition-rule-builder.css',
})
export class ConditionRuleBuilder {
  private conditionRulesManagerService = inject(ConditionRulesManagerService);
  private dataStoreService = inject(DataStoreService);
  private cdr = inject(ChangeDetectorRef);
  isLoading = signal(false);
  isLoadingConditions = signal(false);
  isLoadingOptions = signal(false);
  surveyId = this.dataStoreService.surveyId();

  //

  parentId = input.required<number | string>();
  parentType = input.required<ConditionSetElementType>();
  conditionSetId = input<string | null>();
  conditionSetReady = output<string>();

  // Icons
  readonly Plus = Plus;
  readonly Trash2 = Trash2;
  readonly GripVertical = GripVertical;
  //
  questionOptionsMap = new Map<number, any[]>();
  questionRowsMap = new Map<number, any[]>();

  hasConditions = computed(
    () => this.currentConditionSet().conditions.length > 0,
  );

  allQuestions = computed(() =>
    this.dataStoreService
      .questions()
      .filter((el: Element) => el.element_type === 'questionElement'),
  );

  currentConditionSet = signal<ConditionSet>({
    id: '',
    skip_logic_id: undefined,
    logical_operator: 'AND',
    description: '',
    conditions: [],
    condition_set_element_id: undefined,
    condition_set_element_type: ConditionSetElementType.QUESTION_ELEMENT,
  });

  constructor() {
    effect(() => {
      this.initializeConditionSet();
    });
  }

  //
  async onDescriptionChange(value: string) {
    // Update local state immediately for UI responsiveness
    this.currentConditionSet.update((cs) => ({
      ...cs,
      description: value,
    }));

    if (!this.currentConditionSet().id) {
      console.log('Cannot save description: ConditionSet ID is missing');
      return;
    }

    try {
      await lastValueFrom(
        this.conditionRulesManagerService.updateConditionSet(
          this.currentConditionSet().id,
          {
            description: value,
          },
        ),
      );
      console.log('Description saved successfully');
    } catch (error) {
      console.error('Error saving description:', error);
    }
  }

  async onLogicalOperatorChange(value: 'AND' | 'OR') {
    this.currentConditionSet.update((cs) => ({
      ...cs,
      logical_operator: value,
    }));

    if (!this.currentConditionSet().id) {
      console.log('Cannot save operator: ConditionSet ID is missing');
      return;
    }

    try {
      await lastValueFrom(
        this.conditionRulesManagerService.updateConditionSet(
          this.currentConditionSet().id,
          {
            logical_operator: value,
          },
        ),
      );
      console.log('Operator saved successfully');
    } catch (error) {
      console.error('Error saving logical operator:', error);
    }
  }

  addCondition() {
    const newCondition: Condition = {
      condition_element_id: 0,
      operator: '',
      option_targets: [],
      rank: this.currentConditionSet().conditions.length * 100,
    };

    this.currentConditionSet.update((cs) => ({
      ...cs,
      conditions: [...cs.conditions, newCondition],
    }));
  }

  drop(event: CdkDragDrop<Condition[]>) {
    const currentConditions = [...this.currentConditionSet().conditions];
    moveItemInArray(currentConditions, event.previousIndex, event.currentIndex);
    this.currentConditionSet.update((cs) => ({
      ...cs,
      conditions: currentConditions,
    }));
    this.reorderConditions(event.previousIndex, event.currentIndex);
  }

  async reorderConditions(previousIndex: number, currentIndex: number) {
    if (previousIndex === currentIndex) return;

    try {
      const conditionSetId = this.currentConditionSet().id;
      if (!conditionSetId) return;

      const conditions = [...this.currentConditionSet().conditions];
      const MIN_GAP = 50;

      let newRank: number;
      if (currentIndex === 0) {
        newRank = Math.floor(conditions[1]?.rank / 2) || 50;
        newRank = Math.round(newRank / 50) * 50;
        if (newRank < 50) newRank = 50;
      } else if (currentIndex === conditions.length - 1) {
        const prevRank = conditions[conditions.length - 2]?.rank || 0;
        newRank = prevRank + 100;
      } else {
        const prevRank = conditions[currentIndex - 1].rank;
        const nextRank = conditions[currentIndex + 1].rank;
        newRank = Math.floor((prevRank + nextRank) / 2);
        newRank = Math.round(newRank / 50) * 50;
        if (newRank <= prevRank) newRank = prevRank + 50;
        if (newRank >= nextRank) newRank = nextRank - 50;
      }

      const updates = [
        {
          id: conditions[currentIndex].id as string,
          rank: newRank,
          condition_set_id: conditionSetId,
        },
      ];

      await lastValueFrom(
        this.conditionRulesManagerService.reorderConditions(updates),
      );

      this.currentConditionSet.update((cs) => ({
        ...cs,
        conditions: cs.conditions.map((c, idx) =>
          idx === currentIndex ? { ...c, rank: newRank } : c,
        ),
      }));

      this.cdr.detectChanges();
    } catch (error) {
      console.error('Failed to reorder conditions:', error);
    }
  }

  async onConditionElementChange(condition: Condition) {
    if (typeof condition.condition_element_id === 'string') {
      condition.condition_element_id = parseInt(
        condition.condition_element_id,
        10,
      );
    }

    condition.operator = '';
    condition.option_id = undefined;
    condition.value_manual = undefined;
    condition.option_targets = [];

    const question = this.getQuestionById(condition.condition_element_id);
    if (
      question &&
      [
        QuestionType.SINGLE_CHOICE,
        QuestionType.MULTIPLE_CHOICE,
        QuestionType.DROP_DOWN_MULTIPLE_CHOICE,
        QuestionType.DROP_DOWN_SINGLE_CHOICE,
        QuestionType.CLICK_TO_RANK,
        QuestionType.SINGLE_GRID,
        QuestionType.MULTIPLE_GRID,
      ].includes(question.type)
    ) {
      if (!this.questionOptionsMap.has(question.id)) {
        this.isLoadingOptions.set(true);
        try {
          const settings = await lastValueFrom(
            this.conditionRulesManagerService.getElementSettings(
              question.id,
              question.type,
            ),
          );

          if (
            question.type === QuestionType.SINGLE_GRID ||
            question.type === QuestionType.MULTIPLE_GRID
          ) {
            const rows = await lastValueFrom(
              this.conditionRulesManagerService.getListOptions(
                settings.row_list_id,
              ),
            );

            const columns = await lastValueFrom(
              this.conditionRulesManagerService.getListOptions(
                settings.column_list_id,
              ),
            );

            this.questionRowsMap.set(question.id, rows);
            this.questionOptionsMap.set(question.id, columns);
          } else if (settings && settings.list_id) {
            const options = await lastValueFrom(
              this.conditionRulesManagerService.getListOptions(
                settings.list_id,
              ),
            );

            this.questionOptionsMap.set(question.id, options);
          } else {
            this.questionOptionsMap.set(question.id, []);
          }
        } catch (error) {
          console.error(`Error loading options:`, error);
          this.questionOptionsMap.set(question.id, []);
        } finally {
          this.isLoadingOptions.set(false);
        }
      }
    }

    this.currentConditionSet.update((cs) => ({
      ...cs,
      conditions: [...cs.conditions],
    }));
    await this.onValueChange(condition);
  }

  getQuestionById(questionId: number): any | undefined {
    return this.allQuestions().find((q) => Number(q.id) === Number(questionId));
  }

  async onValueChange(condition: Condition) {
    const hasRequiredData =
      condition.condition_element_id &&
      condition.condition_element_id !== 0 &&
      condition.operator;

    if (hasRequiredData) {
      if (!condition.id) {
        await this.createCondition(condition);
      } else {
        await this.updateCondition(condition);
      }
    }
  }

  async createCondition(condition: Condition) {
    try {
      const payload = {
        skip_logic_id: this.currentConditionSet().skip_logic_id,
        condition_element_id: condition.condition_element_id,
        operator: condition.operator,
        option_id: condition.option_id,
        row_key: condition.row_key,
        value_manual: condition.value_manual,
        option_targets: condition.option_targets,
        logical_operator: this.currentConditionSet().logical_operator,
        description: this.currentConditionSet().description,
        condition_set_element_id:
          this.currentConditionSet().condition_set_element_id,
        condition_set_element_type:
          this.currentConditionSet().condition_set_element_type,
      };

      const response = await lastValueFrom(
        this.conditionRulesManagerService.createCondition(payload),
      );

      condition.id = response.id;

      this.currentConditionSet.update((cs) => ({
        ...cs,
        conditions: cs.conditions.map((c) =>
          c === condition ? { ...c, id: response.id } : c,
        ),
      }));
    } catch (error) {
      console.error('Error creating condition:', error);
      throw error;
    }
  }

  async updateCondition(condition: Condition) {
    try {
      const payload = {
        condition_element_id: condition.condition_element_id,
        operator: condition.operator,
        option_id: condition.option_id,
        row_key: condition.row_key,
        value_manual: condition.value_manual,
        option_targets: condition.option_targets,
        rank: condition.rank,
      };

      await lastValueFrom(
        this.conditionRulesManagerService.updateCondition(
          condition.id as string,
          payload,
        ),
      );
    } catch (error) {
      console.error('Error updating condition:', error);
      throw error;
    }
  }

  getQuestionDisplayText(question: Element): string {
    const truncateText = (html: string, limit = 15): string => {
      if (!html) return '';

      const div = document.createElement('div');
      div.innerHTML = html;
      const plainText = (div.textContent || div.innerText || '')
        .replace(/\s+/g, ' ')
        .trim();

      return plainText.length > limit
        ? plainText.substring(0, limit) + '...'
        : plainText;
    };

    return `${question.name} ${truncateText(question.question_text, 15)}`;
  }

  getOperatorArgType(operatorSlug: string): ArgType {
    return OPERATORS[operatorSlug]?.argType || 'none';
  }

  async onOperatorChange(condition: Condition) {
    condition.option_id = undefined;
    condition.value_manual = undefined;
    condition.option_targets = [];
    if (condition.id && condition.operator) {
      await this.updateCondition(condition);
    } else if (!condition.id && ['ans', 'nans'].includes(condition.operator)) {
      await this.createCondition(condition);
    }

    this.currentConditionSet.update((cs) => ({
      ...cs,
      conditions: [...cs.conditions],
    }));

    if (condition.operator == 'ans') {
      this.onValueChange(condition);
    }
  }

  getOperatorsForQuestion(questionId: number): any[] {
    const question = this.getQuestionById(questionId);
    if (!question) return [];

    const config = QUESTION_TYPE_CONFIG[question.type];
    if (!config) return [];

    return config.operators
      .map((opSlug: string) => OPERATORS[opSlug])
      .filter((op: any) => op !== undefined);
  }

  async updateRangeValue(condition: Condition, key: 'min' | 'max', value: any) {
    if (!condition.value_manual || typeof condition.value_manual !== 'object') {
      condition.value_manual = {};
    }

    condition.value_manual[key] = Number(value);
    await this.onValueChange(condition);
  }

  getQuestionOptions(questionId: number): any[] {
    return this.questionOptionsMap.get(questionId) || [];
  }

  isOptionSelected(condition: Condition, optionId: number | string): boolean {
    if (!condition.option_targets || condition.option_targets.length === 0) {
      return false;
    }

    const optionIdStr = String(optionId);
    const isSelected = condition.option_targets.some(
      (target) => String(target) === optionIdStr,
    );

    return isSelected;
  }

  toggleMultiOption(condition: Condition, optionId: string) {
    if (!condition.option_targets) {
      condition.option_targets = [];
    }

    const index = condition.option_targets.indexOf(optionId);
    if (index > -1) {
      condition.option_targets.splice(index, 1);
    } else {
      condition.option_targets.push(optionId);
    }

    this.onValueChange(condition);
  }

  validateRankValue(cond: Condition) {
    const maxRank = this.getQuestionOptions(cond.condition_element_id).length;
    let rank = Number(cond.value_manual);

    if (isNaN(rank)) {
      rank = 1;
    }

    if (rank < 1) {
      rank = 1;
    }

    if (rank > maxRank) {
      rank = maxRank;
    }

    cond.value_manual = rank;
    this.onValueChange(cond);
  }

  getQuestionRows(questionId: number) {
    const rows = this.questionRowsMap.get(questionId) ?? [];
    return rows;
  }
  async removeCondition(conditionId?: string) {
    if (!conditionId) return;

    try {
      await lastValueFrom(
        this.conditionRulesManagerService.deleteCondition(conditionId),
      );

      this.currentConditionSet.update((cs) => ({
        ...cs,
        conditions: cs.conditions.filter((c) => c.id !== conditionId),
      }));
    } catch (error) {
      console.error('Error deleting condition:', error);
    }
  }

  async loadOptionsForExistingConditions() {
    for (const condition of this.currentConditionSet().conditions) {
      const question = this.getQuestionById(condition.condition_element_id);
      if (
        question &&
        [
          QuestionType.SINGLE_CHOICE,
          QuestionType.MULTIPLE_CHOICE,
          QuestionType.DROP_DOWN_MULTIPLE_CHOICE,
          QuestionType.DROP_DOWN_SINGLE_CHOICE,
          QuestionType.CLICK_TO_RANK,
          QuestionType.SINGLE_GRID,
          QuestionType.MULTIPLE_GRID,
        ].includes(question.type)
      ) {
        if (
          !this.questionOptionsMap.has(question.id) ||
          !this.questionRowsMap.has(question.id)
        ) {
          try {
            const settings = await lastValueFrom(
              this.conditionRulesManagerService.getElementSettings(
                question.id,
                question.type,
              ),
            );

            if (
              question.type === QuestionType.SINGLE_GRID ||
              question.type === QuestionType.MULTIPLE_GRID
            ) {
              const rows = await lastValueFrom(
                this.conditionRulesManagerService.getListOptions(
                  settings.row_list_id,
                ),
              );

              const columns = await lastValueFrom(
                this.conditionRulesManagerService.getListOptions(
                  settings.column_list_id,
                ),
              );

              this.questionRowsMap.set(question.id, rows);
              this.questionOptionsMap.set(question.id, columns);
            } else if (settings && settings.list_id) {
              const options = await lastValueFrom(
                this.conditionRulesManagerService.getListOptions(
                  settings.list_id,
                ),
              );
              this.questionOptionsMap.set(question.id, options);
            } else {
              this.questionOptionsMap.set(question.id, []);
            }
          } catch (error) {
            console.error(
              `Error loading options for question ${question.id}:`,
              error,
            );
            this.questionOptionsMap.set(question.id, []);
          }
        }
      }
    }
  }

  async loadExistingConditionSet(conditionSetId: string) {
    this.isLoadingConditions.set(true);
    try {
      //  First load available questions if not already loaded
      if (this.allQuestions().length === 0) {
        await this.allQuestions();
      }

      const data = await lastValueFrom(
        this.conditionRulesManagerService.getConditionSet(conditionSetId),
      );

      this.currentConditionSet.set({
        id: data.id,
        skip_logic_id: data.skip_logic_id,
        logical_operator: data.logical_operator || 'AND',
        description: data.description || '',
        conditions: (data.conditions || []).map((cond: any) => {
          let valueManual = cond.value_manual;

          if (valueManual && typeof valueManual === 'string') {
            try {
              valueManual = JSON.parse(valueManual);
            } catch {
              // keep original value
            }
          }
          let rowKey = cond.row_key;
          if (rowKey !== undefined && rowKey !== null) {
            rowKey = Number(rowKey);
          } else {
            rowKey = null;
          }

          let optionId = cond.option_id;
          if (optionId !== undefined && optionId !== null) {
            optionId = String(optionId).trim(); // Trim any whitespace
          } else {
            optionId = null;
          }

          let optionTargets = cond.option_targets || [];
          if (Array.isArray(optionTargets)) {
            optionTargets = optionTargets.map((id: any) => String(id).trim());
          }

          return {
            ...cond,
            row_key: rowKey,
            option_id: optionId,
            value_manual: valueManual,
            option_targets: optionTargets,
          };
        }),
        condition_set_element_id: data.condition_set_element_id,
        condition_set_element_type: data.condition_set_element_type,
      });

      //  Load options after conditions are set
      await this.loadOptionsForExistingConditions();
      this.conditionSetReady.emit(data.id);
    } catch (error) {
      console.error('Error loading condition set:', error);
    } finally {
      this.isLoadingConditions.set(false);
    }
  }
  async createNewConditionSet() {
    this.isLoading.set(true);

    const parentId = this.parentId();
    const parentType = this.parentType();

    const payload: any = {
      condition_set_element_type: parentType,
      logical_operator: 'AND',
    };

    if (parentType === 'questionElement') {
      payload.condition_set_element_id = parentId;

      try {
        const response = await lastValueFrom(
          this.conditionRulesManagerService.createConditionSet(payload),
        );

        this.currentConditionSet.update((cs) => ({
          ...cs,
          id: response.id,
          condition_set_element_id: parentId as number,
        }));

        // Emit that condition set is ready
        this.conditionSetReady.emit(response.id);
      } catch (error) {
        console.error('Error creating condition set:', error);
        throw error;
      } finally {
        this.isLoading.set(false);
      }
    }
  }
  async initializeConditionSet() {
    const existingId = this.conditionSetId();
    const parentId = this.parentId();
    const parentType = this.parentType();

    if (existingId) {
      // If conditionSetId was passed directly, use it
      await this.loadExistingConditionSet(existingId);
    } else if (parentId && parentType) {
      // Try to find existing condition set by element ID
      try {
        let foundConditionSet = null;

        if (parentType === 'questionElement') {
          foundConditionSet = await lastValueFrom(
            this.conditionRulesManagerService.getConditionSetByElementId(
              Number(parentId),
            ),
          );
        } else if (parentType === 'logicElement') {
          foundConditionSet = await lastValueFrom(
            this.conditionRulesManagerService.getConditionSetBySkipLogicId(
              parentId.toString(),
            ),
          );
        }

        if (foundConditionSet && foundConditionSet.id) {
          await this.loadExistingConditionSet(foundConditionSet.id);
        } else {
          // No existing condition set, creating new one
          await this.createNewConditionSet();
        }
      } catch (error) {
        console.log('Error finding condition set, creating new one...', error);
        await this.createNewConditionSet();
      }
    } else {
      console.log(' No parentId or parentType provided');
    }
  }

  updateRankValue(cond: Condition, value: any) {
    const maxRank = this.getQuestionOptions(cond.condition_element_id).length;

    let rank = Number(value);

    if (rank < 1) {
      rank = 1;
    }

    if (rank > maxRank) {
      rank = maxRank;
    }

    cond.value_manual = rank;

    this.onValueChange(cond);
  }
  onRowChange(cond: Condition, value: any) {
    cond.row_key = value;

    this.onValueChange(cond);
  }
}
